<template>
    <div>
        <!-- Thumbnail + pin -->
        <v-tooltip :text="tooltipText" location="top">
            <template #activator="{ props: activatorProps }">
                <div v-bind="activatorProps"
                     class="mini-map"
                     role="button"
                     tabindex="0"
                     :aria-label="t('viewDrawing')"
                     @click="showDialog = true"
                     @keyup.enter="showDialog = true">
                    <img :src="thumbnailUrl"
                         :alt="drawingTitle || t('miniMap')"
                         class="mini-map-image"
                         @load="handleImageLoad" />
                    <span v-if="hasPin"
                          class="map-pin"
                          :style="pinStyle"></span>
                </div>
            </template>
        </v-tooltip>

        <!-- Full drawing -->
        <v-dialog v-model="showDialog" max-width="900">
            <v-card :title="drawingTitle" :subtitle="locationTitle">
                <v-card-text>
                    <div class="drawing-wrapper">
                        <img :src="drawingUrl || thumbnailUrl"
                             :alt="drawingTitle || t('viewDrawing')"
                             class="drawing-image"
                             @load="handleImageLoad" />
                        <span v-if="hasPin"
                              class="map-pin map-pin--large"
                              :style="pinStyle"></span>
                    </div>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn :text="t('close')"
                           variant="plain"
                           @click="showDialog = false"></v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

// The pin uses the asset's LOCATION coordinates (locations.locationX / locationY,
// normalized 0-1). Live per-asset x/y would come from the REST search endpoint
// (the issue dashboard's `issueSearch` fetch pattern) - not available offline.
const props = defineProps({
    thumbnailUrl: { type: String, default: null },
    x: { type: Number, default: null },
    y: { type: Number, default: null },
    // Location markers are normalised 0-1; per-item coordinates from the
    // search endpoint are pixels on the drawing image.
    pinUnits: {
        type: String,
        default: 'normalised',
        validator: (value) => ['normalised', 'pixels'].includes(value)
    },
    drawingTitle: { type: String, default: '' },
    drawingUrl: { type: String, default: null },
    locationTitle: { type: String, default: '' }
});

const { t } = useI18n();

const showDialog = ref(false);
// Natural size of the drawing image, needed to place pixel coordinates.
const naturalSize = ref(null);

const hasPin = computed(() => Number.isFinite(props.x) && Number.isFinite(props.y));

const pinStyle = computed(() => {
    if (props.pinUnits === 'pixels') {
        // Without the image's natural size the fraction is unknowable, so hold
        // the pin centred rather than placing it wrongly.
        if (!naturalSize.value) return { left: '50%', top: '50%', opacity: 0 };
        return {
            left: `${((props.x ?? 0) / naturalSize.value.width) * 100}%`,
            top: `${((props.y ?? 0) / naturalSize.value.height) * 100}%`
        };
    }
    return {
        left: `${(props.x ?? 0) * 100}%`,
        top: `${(props.y ?? 0) * 100}%`
    };
});

const handleImageLoad = (event) => {
    const image = event.target;
    if (image.naturalWidth && image.naturalHeight) {
        naturalSize.value = { width: image.naturalWidth, height: image.naturalHeight };
    }
};

const tooltipText = computed(() =>
    [props.drawingTitle, props.locationTitle].filter(Boolean).join(' - ') || t('viewDrawing')
);
</script>

<style scoped>
.mini-map {
    position: relative;
    width: 96px;
    height: 72px;
    border-radius: 6px;
    overflow: hidden;
    cursor: pointer;
    background-color: rgba(127, 127, 127, 0.08);
    border: 1px solid rgba(127, 127, 127, 0.25);
}

.mini-map-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.drawing-wrapper {
    position: relative;
    display: inline-block;
    line-height: 0;
    max-width: 100%;
}

.drawing-image {
    max-width: 100%;
    height: auto;
    display: block;
}

.map-pin {
    position: absolute;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #d03b3b;
    border: 2px solid #ffffff;
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.6);
    transform: translate(-50%, -50%);
    pointer-events: none;
}

.map-pin--large {
    width: 14px;
    height: 14px;
    border-width: 3px;
}
</style>
